// Shared filter / aging helpers used by SupplierPayables page and PayablesCharts.

export type AgingBucket = 'current' | '1_30' | '31_60' | '60_plus';
export type PeriodPreset = 'all' | 'this_month' | 'last_month' | 'this_quarter' | 'custom';

export interface PayableExpenseLike {
  date: string;
  fromDate?: string | null;
  toDate?: string | null;
  dueDate: string | null;
  amount: number;
  amountPaid: number;
  categoryName: string;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function parseIsoDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function expenseAgingBucket(e: PayableExpenseLike, today: Date = new Date()): AgingBucket {
  const ref = parseIsoDate(e.dueDate) ?? parseIsoDate(e.toDate ?? null) ?? parseIsoDate(e.date);
  if (!ref) return 'current';
  const days = Math.floor((startOfDay(today).getTime() - startOfDay(ref).getTime()) / MS_PER_DAY);
  if (days <= 0) return 'current';
  if (days <= 30) return '1_30';
  if (days <= 60) return '31_60';
  return '60_plus';
}

export function expenseDateRange(e: PayableExpenseLike): { start: Date; end: Date } | null {
  const start = parseIsoDate(e.fromDate ?? null) ?? parseIsoDate(e.date);
  if (!start) return null;
  const end = parseIsoDate(e.toDate ?? null) ?? start;
  return { start, end };
}

export function expenseOverlapsWindow(e: PayableExpenseLike, from: Date, to: Date): boolean {
  const range = expenseDateRange(e);
  if (!range) return false;
  return range.start <= to && range.end >= from;
}

export function expenseFullyInside(e: PayableExpenseLike, from: Date, to: Date): boolean {
  const range = expenseDateRange(e);
  if (!range) return false;
  return range.start >= from && range.end <= to;
}

export function resolvePeriodWindow(
  period: PeriodPreset,
  fromIso: string,
  toIso: string,
): { from: Date; to: Date } | null {
  if (period === 'all') return null;
  const today = startOfDay(new Date());
  if (period === 'this_month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from, to };
  }
  if (period === 'last_month') {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from, to };
  }
  if (period === 'this_quarter') {
    const q = Math.floor(today.getMonth() / 3);
    const from = new Date(today.getFullYear(), q * 3, 1);
    const to = new Date(today.getFullYear(), q * 3 + 3, 0);
    return { from, to };
  }
  const from = parseIsoDate(fromIso);
  const to = parseIsoDate(toIso);
  if (!from || !to || from > to) return null;
  return { from, to };
}

// Days from today until due (negative = overdue).
export function daysUntilDue(e: PayableExpenseLike, today: Date = new Date()): number | null {
  const due = parseIsoDate(e.dueDate) ?? parseIsoDate(e.toDate ?? null) ?? parseIsoDate(e.date);
  if (!due) return null;
  return Math.floor((startOfDay(due).getTime() - startOfDay(today).getTime()) / MS_PER_DAY);
}
